/* eslint-disable no-param-reassign */

import { createSlice } from '@reduxjs/toolkit';

import { fetchOrder } from '../thunks/orders';

import {
  buildLeg,
  buildTraveler,
  buildLegShipment,
  buildOrder,
} from '../state';

const initialState = {
  status: 'idle',
  cart: {},
  order: {},
  shipments: {},
  legs: [],
  travelers: [],
  reason: null,
  tourEnabled: false,
  tourContent: [],
  watchedContent: {},
  products: {},
  showTour: undefined,
};

const orderSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    appendLeg(state, { payload }) {
      const { leg, shipments } = payload;

      state.legs = [...state.legs, buildLeg(leg)];
      state.shipments = [
        ...state.shipments,
        shipments.map((shipment) => buildLegShipment(shipment)),
      ];
    },
    appendTraveler(state, { payload }) {
      const ids = state.travelers.map((traveler) => traveler.id);

      const traveler = buildTraveler(payload);

      if (!ids.includes(traveler.id)) {
        state.travelers = [...state.travelers, traveler];
      } else {
        const travelers = state.travelers.filter((t) => t.id !== traveler.id);
        // rebuild the traveler list with the updated traveler
        if (travelers.length > 0) {
          state.travelers = [...travelers, traveler];
        } else {
          state.travelers = [traveler];
        }
      }
    },
    updateLegs(state, { payload: { legs, shipments } }) {
      state.legs = legs.map((leg) => buildLeg(leg));

      if (shipments !== undefined) {
        state.shipments = shipments.map(
          (shipment) => buildLegShipment(shipment),
        );
      }
    },
    setShowTour(state, { payload: value }) {
      state.showTour = value;
    },
    updateWatchedContent(state, { payload: { watched: watchedContent } }) {
      state.watchedContent = watchedContent;
    },
  },
  extraReducers: {
    [fetchOrder.pending]: (state) => {
      state.status = 'loading';
    },
    [fetchOrder.fulfilled]: (state, action) => {
      const { payload } = action;

      const {
        legs,
        travelers,
        shipments,
        applicable_for_edit: applicableForEdit,
        reason,
        num_shipments: numShipments,
        enqueued_shipments: enqueuedShipments,
        live_shipments: liveShipments,
        ineligible_shipments: ineligibleShipments,
        tour_enabled: tourEnabled,
        tour_content: tourContent,
        watched_content: watchedContent,
        products,
      } = payload;

      if (applicableForEdit) {
        state.cart = { id: payload.id, token: payload.cart_token };
        state.order = buildOrder(payload);
        state.legs = legs.map((leg) => buildLeg(leg));
        state.travelers = travelers.map((traveler) => buildTraveler(traveler));
        state.shipments = shipments.map(
          (shipment) => buildLegShipment(shipment),
        );
        state.status = 'succeeded';
        state.tourEnabled = tourEnabled;
        state.tourContent = tourContent;
        state.watchedContent = watchedContent;
        state.products = products;
      } else {
        state.status = 'empty';
        state.reason = reason;
        state.numShipments = numShipments;
        state.enqueuedShipments = enqueuedShipments;
        state.liveShipments = liveShipments;
        state.ineligibleShipments = ineligibleShipments;
        state.tourEnabled = tourEnabled;
        state.tourContent = tourContent;
        state.watchedContent = watchedContent;
      }
    },
    [fetchOrder.rejected]: (state) => {
      state.status = 'failed';
    },
  },
});

export const {
  appendLeg,
  appendTraveler,
  updateLegs,
  setShowTour,
  setWatchedTour,
  updateWatchedContent,
} = orderSlice.actions;

export default orderSlice.reducer;
